export type GroupByMode = "none" | "product_group";

const UNASSIGNED_KEY = "__unassigned__";

// The minimal row shape grouping needs. Both ProductRow (by-channel/top-down)
// and StockMatrixRow (by-warehouse) satisfy it, so the same grouping logic
// serves every inventory view.
export interface GroupableRow {
  product_node_id?: string | null;
  product_node_path?: string | null;
  product_node_order?: number | null;
}

export interface Group<T> {
  key: string;
  label: string;
  rows: T[];
}

interface KeyLabel {
  key: string | null;
  label: string | null;
}

function keyLabelFor(row: GroupableRow, mode: GroupByMode): KeyLabel {
  switch (mode) {
    case "product_group":
      return { key: row.product_node_id ?? null, label: row.product_node_path ?? null };
    default:
      return { key: null, label: null };
  }
}

/**
 * Group product rows for display. Rows with a null/empty key fall into an
 * "Unassigned" group. Groups follow the product group tree order
 * (product_node_order), with Unassigned always last. Row order within each
 * group is preserved (server already sorts by inventory desc).
 */
export function groupProducts<T extends GroupableRow>(
  products: T[],
  mode: GroupByMode,
): Group<T>[] {
  const byKey = new Map<string, Group<T>>();
  const orderOf = new Map<string, number>();

  for (const row of products) {
    const { key, label } = keyLabelFor(row, mode);
    const hasKey = key !== null && key !== "";
    const groupKey = hasKey ? key : UNASSIGNED_KEY;
    const groupLabel = hasKey ? (label ?? key) : "Unassigned";

    if (!orderOf.has(groupKey)) {
      orderOf.set(groupKey, (row as GroupableRow).product_node_order ?? Number.MAX_SAFE_INTEGER);
    }
    const existing = byKey.get(groupKey);
    if (existing) {
      existing.rows.push(row);
    } else {
      byKey.set(groupKey, { key: groupKey, label: groupLabel, rows: [row] });
    }
  }

  return Array.from(byKey.values()).sort((a, b) => {
    if (a.key === UNASSIGNED_KEY) return 1;
    if (b.key === UNASSIGNED_KEY) return -1;
    return (orderOf.get(a.key) ?? 0) - (orderOf.get(b.key) ?? 0);
  });
}
